import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { BrandLockup } from "@/components/LogoMark";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ROLES = [
  { value: "arbitrator", label: "Arbitrator" },
  { value: "advocate", label: "Advocate" },
  { value: "client", label: "Client" },
];

export default function DemoAccess() {
  const navigate = useNavigate();
  const { setDemoUser } = useAuth();
  const [key, setKey] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const enter = async (role) => {
    setBusy(role);
    setError("");
    try {
      const { data } = await api.post("/demo/session", { key: key.trim(), role });
      setDemoUser(data);
      navigate("/dashboard");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 grain flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md relative z-10" data-testid="demo-access-page">
        <BrandLockup dark size="text-2xl" mark="h-8 w-8" />
        <p className="text-xs uppercase tracking-[0.3em] text-amber-200/70 mt-8">Private</p>
        <h1 className="font-display text-3xl text-white mt-2">Demo access</h1>
        <p className="text-sm text-slate-400 mt-4 leading-relaxed">
          Enter your demo access key to open the app as any role. This page is not linked from
          anywhere and no credentials are shown on the public sign-in screen.
        </p>

        <div className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Demo access key</Label>
            <Input data-testid="demo-key-input" type="password" value={key}
              onChange={(e) => setKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && key.trim() && enter("arbitrator")}
              className="rounded-none bg-slate-900 border-slate-700 text-white focus:ring-2 focus:ring-blue-700"
              placeholder="••••••••••••" />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="demo-error">{error}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {ROLES.map((r) => (
              <Button key={r.value} data-testid={`demo-enter-${r.value}`} disabled={!key.trim() || !!busy}
                onClick={() => enter(r.value)}
                className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-11">
                {busy === r.value ? "…" : r.label}
              </Button>
            ))}
          </div>
          <p className="flex items-start gap-2 text-[11px] text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Five wrong attempts lock this page for 15 minutes.
          </p>
        </div>
      </div>
    </div>
  );
}
